import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { NavFlag } from '../Nav/ViewComponents';
import { LanguageSelector as NavLanguage } from '../Nav/NavLanguage';
import config, { currentCountryCode } from '../../config';
import Countries from '../Common/Country/Country.APP_ENV.ajs?preParser=preProcess';
import { withStoreDetail } from '../Common/Providers/WithStoreDetail';
import {
  withTranslation,
  TransLink,
  TransAnchor,
  HTMLTrans,
} from '../Common/Translations';
import getOS from '../Common/Helpers/GetOs';
import { willHideOnMuslimPro, isMuslimProStore, includeProProgramme } from '../Common/Store/Store';

const ExternalLink = (props) => {
  const { text = '', icon, url } = props;

  return (
    <div>
      <a className="footer--inherit-color" href={url} target="_blank">
        <i className={icon}></i> {text}
      </a>
    </div>
  );
};

const HelpSection = willHideOnMuslimPro(({ t, renderList }) =>
  renderList({
    title: t('footer.renderhelpsection_title', 'Help'),
    list: [
      {
        text: t('footer.renderhelpsection_faq', 'FAQ'),
        href: '/faq',
      },
      {
        text: t('footer.renderhelpsection_contactus', 'Contact Us'),
        href: '/support/contact',
      },
      {
        text: t('footer.renderhelpsection_feedback', 'Feedback'),
        href: '/support/feedback',
      },
      {
        text: t(
          'footer.renderhelpsection_shippinginformation',
          'Shipping Information'
        ),
        href: '/product/shipping-info',
      },
      {
        text: t('footer.renderhelpsection_trackmyorder', 'Track My Order'),
        href: '/support/track-order',
      },
    ],
  })
);

const AboutSection = willHideOnMuslimPro(({ t, renderList }) =>
  renderList({
    title: t('footer.renderaboutsection_title', 'About'),
    list: [
      {
        text: t('footer.renderaboutsection_aboutus', 'About Us'),
        href: '/about-photobook',
      },
      {
        text: t('footer.renderaboutsection_career', 'Career'),
        href: '/careers',
      },
      {
        text: t('footer.renderaboutsection_qualityguarantee', 'Our Guarantees'),
        href: '/guarantee',
      },
    ],
  })
);

const ShopSection = willHideOnMuslimPro(({ t, renderList }) =>
  renderList(
    {
      title: t('footer.rendershopsection_title', 'Shop'),
      list: [
        {
          text: t('footer.rendershopsection_photobooks', 'Photobooks'),
          href: '/photobooks',
        },
        {
          text: t('footer.rendershopsection_photoalbums', 'Flush Mount Books'),
          href: '/flushmount',
        },
        {
          text: t('footer.rendershopsection_canvasprints', 'Canvas Prints'),
          href: '/canvas-prints',
        },
        {
          text: t('footer.rendershopsection_phototiles', 'Photo Tiles'),
          href: '/home-decor/photo-tiles',
        },
        {
          text: t('footer.rendershopsection_metalprints', 'Metal Prints'),
          href: '/home-decor/metal-prints',
        },
        {
          text: t('footer.rendershopsection_framedprints', 'Framed Prints'),
          href: '/home-decor/framed-prints',
        },
        {
          text: t('footer.rendershopsection_posterprints', 'Poster Prints'),
          href: '/prints/poster-prints',
        },
        {
          text: t('footer.rendershopsection_photoprints', 'Photo Prints'),
          href: '/prints/photo-prints',
        },
        {
          text: t('footer.rendershopsection_wallstickers', 'Wall Stickers'),
          href: '/home-decor/wall-stickers',
        },
        {
          text: t(
            'footer.rendershopsection_invitationcards',
            'Invitation Cards'
          ),
          href: '/invitationcards',
        },
      ],
    },
    true
  )
);

const GiftSection = willHideOnMuslimPro(({ t, renderList }) =>
  renderList(
    {
      title: t('footer.rendergiftsection_title', 'Gifts'),
      list: [
        {
          text: t('footer.rendergiftsection_photogifts', 'Photo Gifts'),
          href: '/photo-gifts',
        },
        {
          text: t('footer.rendergiftsection_custommugs', 'Custom Mugs'),
          href: '/photo-gifts/mugs',
        },
        {
          text: t(
            'footer.rendergiftsection_canvastotebags',
            'Canvas Tote Bags'
          ),
          href: '/photo-gifts/canvas-tote-bags',
        },
        {
          text: t('footer.rendergiftsection_custompuzzles', 'Custom Puzzles'),
          href: '/photo-gifts/photo-puzzles',
        },
        {
          text: t('footer.rendergiftsection_luggagetags', 'Luggage Tags'),
          href: '/gifts/luggage-tags',
        },
        {
          text: t('footer.rendergiftsection_customtshirts', 'Custom T-Shirts'),
          href: '/clothing/t-shirts',
        },
        {
          text: t(
            'footer.rendergiftsection_moleskinenotebooks',
            'Moleskine Notebooks'
          ),
          href: '/stationery/moleskine',
        },
        {
          text: t('footer.rendergiftsection_mothersdaycards', 'Water Bottles'),
          href: '/photo-gifts/bottles',
        },
        {
          text: t('footer.rendergiftsection_greetingcards', 'Greeting Cards'),
          href: '/greetingcards',
        },
        {
          text: t('footer.rendergiftsection_birthdaycards', 'Birthday Cards'),
          href: '/greetingcards/celebrations/happy-birthday',
        },
      ],
    },
    true
  )
);

const ExploreSection = willHideOnMuslimPro(({ t, renderList }) =>
  renderList(
    {
      title: t('footer.renderexploresection_title', 'Explore'),
      list: (config.bulkOrderDisabled
        ? []
        : [
            {
              text: t('footer.renderexploresection_bulkorder', 'Bulk Order'),
              href: config.bulkOrderPath
                ? config.bulkOrderPath
                : '/bulk-discount',
            },
          ]
      ).concat([
        {
          text: t(
            'footer.renderexploresection_bulkdiscountbusiness',
            'Business'
          ),
          href: '/business-solutions',
        },
        {
          text: t('footer.renderexploresection_blog', 'Blog'),
          href: '/blog',
        },
        {
          text: t('footer.renderexploresection_pros', 'For the PROs'),
          href: includeProProgramme()
            ? '/pro-photographer'
            : '/pro-programme',
        },
        {
          text: t('footer.renderexploresection_quickguide', 'Quick Guide'),
          href: '/photobook-designer/quick-guide-software',
        },
        {
          text: t('footer.renderexploresection_affiliate', 'Affiliate Program'),
          href: '/affiliate',
        },
      ]),
    },
    true
  )
);

const ProgramSection = willHideOnMuslimPro(({ t, renderList }) =>
  renderList({
    title: t('footer.renderprogramsection_title', 'Program'),
    list: (config.bulkOrderDisabled
      ? []
      : [
          {
            text: t(
              'footer.renderprogramsection_bulkdiscount',
              'Bulk Discount'
            ),
            href: config.bulkOrderPath
              ? config.bulkOrderPath
              : '/bulk-discount',
          },
        ]
    ).concat([
      {
        text: t('footer.renderprogramsection_affiliate', 'Affiliate Program'),
        href: '/affiliate',
      },
    ]),
  })
);

class Footer extends Component {
  constructor(props) {
    super(props);

    this.state = { subscriptionEmail: '' };

    this.handleChange = this.handleChange.bind(this);
    this.submitNewsletterSubscription =
      this.submitNewsletterSubscription.bind(this);
  }

  static propTypes = {
    t: PropTypes.func,
  };

  handleChange(event) {
    this.setState({ subscriptionEmail: event.target.value });
  }

  _renderSectionList(section, refresh = false) {
    return (
      <ul>
        <li className="section-title ProximaNova-Bold d-block mb-2">
          {section.title}
        </li>
        {section.list.map((item, index) => {
          let urlDecorator = item.text;
          if (item.href && item.href.length > 0) {
            urlDecorator = refresh ? (
              <TransAnchor url={item.href} name={item.text} />
            ) : (
              <TransLink url={item.href} name={item.text} />
            );
          }
          return <li key={index}>{urlDecorator}</li>;
        })}
      </ul>
    );
  }

  _renderCollapseSectionList(t, section, refresh = false) {
    const sectionTitle = section.title.toLowerCase();
    const sectionList = section;
    if (section.title === t('footer.renderprogramsection_title', 'Program')) {
      const countries = ['PBWW', 'PBEU', 'PBAE'];
      const excludeAffiliateCountries = countries.includes(config.storeCode);

      if (excludeAffiliateCountries) {
        const program = sectionList.list.filter(
          (p) =>
            p.text !==
            t('footer.renderprogramsection_affiliate', 'Affiliate Program')
        );
        sectionList.list.splice(0, sectionList.list.length, ...program);
      }
    }
    return (
      <div className="mb-4">
        <a
          className="section-title ProximaNova-Bold d-lg-none d-block pb-2"
          data-toggle="collapse"
          href={`#${sectionTitle}`}
          aria-expanded="false"
        >
          {sectionList.title}
        </a>
        <div className="section-title ProximaNova-Bold d-none d-lg-block mb-2">
          {sectionList.title}
        </div>
        <ul id={sectionTitle} className="d-lg-block collapse">
          {sectionList.list.map((item, index) => {
            let urlDecorator = item.text;
            if (item.href && item.href.length > 0) {
              urlDecorator = refresh ? (
                <TransAnchor
                  url={item.href}
                  target={item.target}
                  name={item.text}
                />
              ) : (
                <TransLink url={item.href} name={item.text} />
              );
            }
            return <li key={index}>{urlDecorator}</li>;
          })}
        </ul>
      </div>
    );
  }

  _renderPrivacyPolicySection(t) {
    const privacy = [
      {
        text: t('footer.renderprivacypolicysection_privacy', 'Privacy Policy'),
        url: '/return-policy/privacy-policy',
      },
      {
        text: t('footer.renderprivacypolicysection_return', 'Return Policy'),
        url: '/return-policy',
      },
      {
        text: t('footer.renderprivacypolicysection_termsofuse', 'Terms of Use'),
        url: '/return-policy/terms-of-use',
      },
    ];

    const privacyWithCookiePolicy = [
      {
        text: {
          id: 'footer.privacywithcookiepolicy_privacy',
          defaultMsg: 'Privacy Policy',
        },
        url: '/return-policy/privacy-policy',
      },
      {
        text: {
          id: 'footer.privacywithcookiepolicy_cookie',
          defaultMsg: 'Cookie Policy',
        },
        url: '/cookie-policy',
      },
      {
        text: {
          id: 'footer.privacywithcookiepolicy_return',
          defaultMsg: 'Return Policy',
        },
        url: '/return-policy',
      },
      {
        text: {
          id: 'footer.privacywithcookiepolicy_termsofuse',
          defaultMsg: 'Terms of Use',
        },
        url: '/return-policy/terms-of-use',
      },
    ];

    const cookiePolicyCountries = ['PBWW', 'PBUK', 'PBEU', 'PBIE'];
    const isCookiePolicyCountries = cookiePolicyCountries.includes(
      config.storeCode
    );

    const privacyFooter = privacy.map((item, index) => (
      <TransLink className="text-gray-lighten-1" key={index} url={item.url}>
        {' '}
        | {item.text}
      </TransLink>
    ));

    const privacyFooterWithCookiePolicy = privacyWithCookiePolicy.map(
      (item, index) => (
        <TransLink className="text-gray-lighten-1" key={index} url={item.url}>
          {' '}
          | {t(item.text.id, item.text.defaultMsg)}
        </TransLink>
      )
    );

    const year = new Date().getFullYear();

    return (
      <div className="align-self-center">
        {t(
          'footer.copyright_statement',
          `Copyright © 2005 - ${year} Photobook Worldwide. All rights reserved.`,
          { year: year }
        )}
        {isCookiePolicyCountries ? (
          <span>{privacyFooterWithCookiePolicy}</span>
        ) : (
          <span>{privacyFooter}</span>
        )}
      </div>
    );
  }

  submitNewsletterSubscription(e) {
    e.preventDefault();
    const email = this.state.subscriptionEmail;
    const countries = Countries.data;
    const countryName = countries.find(
      (country) => country.id === currentCountryCode
    ).label;

    const payload = {
      email,
      country_name: countryName,
      country_code: currentCountryCode,
      channel_source: 'footer',
    };

    $.ajax({
      url: `${config.localeApi}/newsletter/subscribe/`,
      type: 'POST',
      dataType: 'json',
      contentType: 'application/json',
      jsonp: false,
      data: JSON.stringify(payload),
    })
      .done((resp) => {
        toastr.success(
          this.props.t(
            'footer.subscribe_successful',
            'Subscribed successfully!'
          )
        );
      })
      .fail(() => {
        toastr.error(
          this.props.t(
            'footer.subscribe_fail',
            'Newsletter subscription failed'
          )
        );
      });
  }

  _renderSocialMediaSection(t) {
    const social = [
      {
        icon: 'fa fa-facebook',
        url: t(
          'footer.social_media_facebook_url',
          'https://www.facebook.com/photobookww'
        ),
      },
      {
        icon: 'fa fa-instagram',
        url: t(
          'footer.social_media_instagram_url',
          'https://instagram.com/photobookww/'
        ),
      },
      {
        icon: 'fa fa-pinterest-p',
        url: 'https://www.pinterest.com/photobookww/',
      },
      {
        icon: 'fa fa-twitter',
        url: 'https://twitter.com/photobookww',
      },
      {
        icon: 'fa fa-linkedin',
        url: 'https://www.linkedin.com/company/photobook-worldwide',
      },
    ];

    return social.map((item, index) => (
      <ExternalLink
        key={index}
        icon={`col-1 ${item.icon} social__media-font`}
        url={item.url}
      />
    ));
  }

  render() {
    const { t, store = {}, isIOS = getOS() === 'iOS' } = this.props;
    const {
      currency: { symbol = '', code = '' } = {},
      flag = '',
      name = 'Photobook',
    } = store;
    const countryName = name.slice(10);

    const navFlag = !isMuslimProStore(store) && (
      <div className="row d-block d-lg-none">
        <NavFlag
          isMobile={true}
          flag={flag}
          countryName={countryName}
          currency={`${symbol} ${code}`}
        />
        {this.props.languages.length > 1 && (
          <NavLanguage languages={this.props.languages} isMobile={true} />
        )}
      </div>
    );

    return (
      <footer className="py-6 py-lg-7 content-visibility-auto">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="row">
                <div className="col-12 col-lg-3">
                  <div className="row">
                    <div className="col-6 col-lg-12">
                      <HelpSection
                        store={store}
                        t={t}
                        renderList={this._renderSectionList}
                      />
                    </div>
                    <div className="col-6 col-lg-12">
                      <AboutSection
                        store={store}
                        t={t}
                        renderList={this._renderSectionList}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-3">
                  <ShopSection
                    store={store}
                    t={t}
                    renderList={this._renderCollapseSectionList.bind(this, t)}
                  />
                </div>
                <div className="col-lg-3">
                  <GiftSection
                    store={store}
                    t={t}
                    renderList={this._renderCollapseSectionList.bind(this, t)}
                  />
                </div>
                <div className="col-12 col-lg-3">
                  <div className="row">
                    <div className="col-12">
                      <ExploreSection
                        store={store}
                        t={t}
                        renderList={this._renderCollapseSectionList.bind(
                          this,
                          t
                        )}
                      />
                    </div>
                    <div className="col-12">
                      <ul className="row">
                        <li className="col-12 section-title ProximaNova-Bold d-block mb-2">
                          <HTMLTrans id="footer.downloadsection_title">
                            Download
                          </HTMLTrans>
                        </li>
                        <li className="col-6 col-lg-12">
                          <ExternalLink
                            text="App Store"
                            url="https://itunes.apple.com/app/id1183692363"
                            icon="fa fa-apple fa-fw"
                          />
                        </li>
                        {!isIOS && (
                          <li className="col-6 col-lg-12">
                            <ExternalLink
                              text="Google Play"
                              url="https://play.google.com/store/apps/details?id=com.photobook.android"
                              icon="fa fa-android fa-fw"
                            />
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <ul className="mb-2">
                <li className="ProximaNova-Bold">
                  <HTMLTrans id="footer.firsttoknow_title">
                    BE THE FIRST TO KNOW
                  </HTMLTrans>
                </li>
              </ul>
              <p className="mt-2">
                <HTMLTrans
                  id="footer.subscribe_to_newsletter"
                  variables={{ name: name }}
                >
                  Join our newsletter for the first look at new personalised
                  products, design tips and exclusive offers from {name}.
                </HTMLTrans>
              </p>
              <form id="subscribe" onSubmit={this.submitNewsletterSubscription}>
                <div className="row">
                  <div className="col-12 col-lg-9">
                    <input
                      type="email"
                      className="form-control form-control mb-3"
                      id="subscribe-email"
                      placeholder="Email address"
                      onChange={this.handleChange}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-4">
                    <input
                      type="submit"
                      className="btn btn-default btn-primary mb-5 mb-lg-3"
                      id="subscribe-submit"
                      value="SIGN ME UP"
                    />
                  </div>
                </div>
              </form>
              {navFlag}
            </div>
          </div>
          <div
            id="social-media"
            className="row my-5 my-lg-4 justify-content-center justify-content-lg-start"
          >
            {this._renderSocialMediaSection(t)}
          </div>
          <div
            id="privacy-policy"
            className="d-lg-flex justify-content-between align-items-center text-gray-lighten-1 text-center text-lg-left"
          >
            <div className="mb-3 mb-lg-0">
              {this._renderPrivacyPolicySection(t)}
              {config.storeCode === 'PBMY' && (
                <address>
                  <HTMLTrans id="contact_us.address_no_map">
                    7, Jalan TP 2, Sime UEP Industrial Park,47600 Subang Jaya,
                    Selangor, Malaysia
                  </HTMLTrans>
                </address>
              )}
            </div>
          </div>
        </div>
      </footer>
    );
  }
}

export default withTranslation('footer')(withRouter(withStoreDetail(Footer)));
export const undecorated = Footer;



// WEBPACK FOOTER //
// ./src/Layout/Footer.js